import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
  const { login, loginWithGoogle, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/admin", { replace: true });
    else setError(res.error);
  };

  return (
    <div className="min-h-screen bg-navy-deep flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo-monogram.png" alt="West Ouachita Cabinets" className="h-24 w-auto mx-auto rounded-[2px] ring-1 ring-gold/30" />
          <h1 className="font-serif text-3xl text-ivory mt-4">Leads Dashboard</h1>
          <p className="text-[#A1B0C4] text-sm mt-1">Sign in to manage consultation requests</p>
        </div>
        <form onSubmit={submit} className="bg-navy-surface border border-gold/25 p-8 space-y-5" data-testid="admin-login-form">
          <div className="space-y-1.5">
            <Label className="text-[#A1B0C4] text-xs tracking-wider uppercase">Email</Label>
            <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="rounded-none bg-navy border-gold/25 text-ivory focus-visible:ring-gold" placeholder="you@email.com" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#A1B0C4] text-xs tracking-wider uppercase">Password</Label>
            <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="rounded-none bg-navy border-gold/25 text-ivory focus-visible:ring-gold" placeholder="••••••••" />
          </div>
          {error && <p className="text-red-400 text-sm" data-testid="login-error">{error}</p>}
          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-gold text-navy-deep font-semibold tracking-luxe uppercase text-sm px-8 py-4 rounded-none hover:bg-gold-light transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Sign In
          </button>

          <div className="flex items-center gap-3 py-1">
            <span className="h-px flex-1 bg-gold/20" />
            <span className="text-[#66768c] text-xs tracking-luxe uppercase">or</span>
            <span className="h-px flex-1 bg-gold/20" />
          </div>

          <button
            data-testid="login-google"
            type="button"
            onClick={loginWithGoogle}
            className="w-full bg-white text-navy font-medium tracking-wide text-sm px-8 py-4 rounded-none hover:bg-ivory transition-colors flex items-center justify-center gap-3"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
            Continue with Google
          </button>
        </form>
        <p className="text-[#66768c] text-xs text-center mt-4">Google sign-in is limited to authorized team accounts.</p>
      </div>
    </div>
  );
}
